import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, CheckCircle, Send } from 'lucide-react';
import { fetchDestinations, resolveImageUrl } from '../lib/travelApi';
import { toast } from 'sonner';

const ActivitiesPage = () => {
  const { destinationId } = useParams();
  const navigate = useNavigate();
  const [destination, setDestination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedActivities, setSelectedActivities] = useState([]);

  useEffect(() => {
    fetchDestinations()
      .then((data) => {
        setDestination(data.find((d) => String(d.id) === String(destinationId)) || null);
      })
      .catch(() => setDestination(null))
      .finally(() => setLoading(false));
  }, [destinationId]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!destination) {
    return <div className="min-h-screen flex items-center justify-center">Destination not found</div>;
  }

  const handleActivityToggle = (activityId) => {
    setSelectedActivities(prev => 
      prev.includes(activityId) 
        ? prev.filter(id => id !== activityId)
        : [...prev, activityId]
    );
  };

  const handleContinue = () => {
    if (selectedActivities.length === 0) {
      toast.error('Please select at least one activity');
      return;
    }
    navigate('/contact', { 
      state: { 
        destination: destination.name, 
        activities: destination.activities.filter(a => selectedActivities.includes(a.id)),
        inquiryType: 'activities'
      }
    });
  };

  return (
    <section className="min-h-screen py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <Button
          variant="outline"
          onClick={() => navigate('/destinations')}
          className="mb-8"
          data-testid="back-to-destinations"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Destinations
        </Button>

        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Activities in {destination.name}
          </h1>
          <p className="text-xl text-gray-600">
            Select the activities you&apos;d like to experience
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto mb-24">
          {destination.activities.map((activity) => {
            const isSelected = selectedActivities.includes(activity.id);
            return (
              <Card
                key={activity.id}
                data-testid={`activity-card-${activity.id}`}
                className={`cursor-pointer transition-all duration-300 overflow-hidden ${
                  isSelected ? 'ring-4 ring-amber-500 shadow-xl' : 'hover:shadow-lg'
                }`}
                onClick={() => handleActivityToggle(activity.id)}
              >
                {/* Activity Image */}
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={resolveImageUrl(activity.image)}
                    alt={activity.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  {/* Selection indicator overlay */}
                  {isSelected && (
                    <div className="absolute inset-0 bg-amber-500/20 flex items-center justify-center">
                      <div className="bg-amber-500 rounded-full p-2">
                        <CheckCircle className="w-8 h-8 text-white" />
                      </div>
                    </div>
                  )}
                </div>
                
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        {activity.name}
                      </h3>
                      <p className="text-gray-600 text-sm line-clamp-2">
                        {activity.description}
                      </p>
                      {activity.price > 0 && (
                        <p className="mt-2 text-amber-600 font-bold text-sm" data-testid={`activity-price-${activity.id}`}>
                          ₹{Number(activity.price).toLocaleString('en-IN')}
                          <span className="text-xs text-gray-400 font-normal"> / person</span>
                        </p>
                      )}
                    </div>
                    <div className="ml-3">
                      {isSelected ? (
                        <div className="w-6 h-6 bg-amber-600 rounded flex items-center justify-center">
                          <CheckCircle className="w-5 h-5 text-white" />
                        </div>
                      ) : (
                        <div className="w-6 h-6 border-2 border-gray-300 rounded hover:border-amber-400 transition-colors"></div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Fixed Bottom Bar */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-40">
          <div className="container mx-auto flex items-center justify-between max-w-7xl">
            <div>
              <p className="text-lg font-semibold text-gray-900">
                {selectedActivities.length} {selectedActivities.length === 1 ? 'Activity' : 'Activities'} Selected
              </p>
              <p className="text-sm text-gray-600">
                {selectedActivities.length > 0 
                  ? 'Ready to send your selection?' 
                  : 'Select activities to continue'}
              </p>
            </div>
            <Button
              onClick={handleContinue}
              size="lg"
              disabled={selectedActivities.length === 0}
              className="bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
              data-testid="send-activities-request"
            >
              <Send className="w-4 h-4 mr-2" />
              Send Request
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ActivitiesPage;
